'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Modal } from '@common/components';
import { cn, isEdge } from '@core/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useState, useTransition } from 'react';
import { UnauthQueryOptions } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { buildGiftOrderSearchParams } from '@/app/donation/_utils/giftOrder';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { DONATION_ROUTES, STORAGE_KEY } from '@/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { GIFT_VALUE_RATE_PERCENT } from '@/constants/hometownDonationPolicy';
import { useCheckKakaobank } from '@/hooks/useCheckKakaobank';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useLoginGuard } from '@/hooks/useLoginGuard';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useVerificationCheck } from '@/hooks/useVerificationCheck';
import { useAuthStore } from '@/stores/AuthStore';
import { gtmDatasets } from '@/utils/analytics/gtm';
import { DonationReservationCompleteModal } from '../DonationReservationCompleteModal';
import { DonationReservationModal } from '../DonationReservationModal';
import { useIsDonationHistory } from './_hooks/useIsDonationHistory';
import { AgreementSection } from './AgreementSection';
import { PrefillForm } from './PrefillForm';
interface DonationButtonProps {
  institutionId: number;
  institutionSlugId?: string;
  donationBusinessId?: number;
  institutionName: string;
  disabled?: boolean;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}
export const DonationButton = t0 => {
  const $ = _c(98);
  const {
    institutionId,
    institutionSlugId,
    donationBusinessId,
    institutionName,
    disabled,
    children,
    style,
    className
  } = t0;
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const isLogin = useAuthStore(_temp);
  const isKakaobank = useCheckKakaobank();
  const {
    isDonationHistory
  } = useIsDonationHistory();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = isEdge();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const isEdgeBrowser = t1;
  const [, startTransition] = useTransition();
  const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);
  const [isReservationCompleteModalOpen, setIsReservationCompleteModalOpen] = useState(false);
  const router = useCustomRouter();
  const loginGuard = useLoginGuard();
  let t2;
  if ($[1] !== isLogin) {
    t2 = {
      enabled: isLogin
    };
    $[1] = isLogin;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    checkVerification,
    isLoading: isVerificationLoading
  } = useVerificationCheck(t2);
  const [step, setStep] = useState("closed");
  const [isEdgeModal, setIsEdgeModal] = useState(false);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      ...UnauthQueryOptions["get:/wello/v2/allows/control-managements"](),
      select: _temp2,
      staleTime: 0,
      gcTime: 0
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    data: donationReservation
  } = useSuspenseQuery(t3);
  let t4;
  if ($[4] !== checkVerification || $[5] !== donationReservation || $[6] !== loginGuard) {
    t4 = () => {
      if (isEdgeBrowser) {
        setIsEdgeModal(true);
        return;
      }
      if (!loginGuard()) {
        return;
      }
      if (checkVerification()) {
        return;
      }
      if (donationReservation) {
        setStep("closed");
        setIsReservationModalOpen(true);
        return;
      }
      setStep("product-check");
    };
    $[4] = checkVerification;
    $[5] = donationReservation;
    $[6] = loginGuard;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleStart = t4;
  let t5;
  if ($[8] !== isDonationHistory || $[9] !== isKakaobank) {
    t5 = () => {
      if (isKakaobank) {
        setStep("prefill");
        return;
      }
      const isAgreement = sessionStorage.getItem(STORAGE_KEY.DONATION_AGREEMENT);
      if (!isDonationHistory && !isAgreement) {
        setStep("agreement");
        return;
      }
      setStep("prefill");
    };
    $[8] = isDonationHistory;
    $[9] = isKakaobank;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleDonationWithoutGift = t5;
  let t6;
  if ($[11] !== donationBusinessId || $[12] !== institutionId || $[13] !== institutionName || $[14] !== router) {
    t6 = () => {
      sessionStorage.setItem(STORAGE_KEY.DONATION_AGREEMENT, "true");
      router.push(DONATION_ROUTES.HOMETOWN_BUSINESS_ORDER.withSearchParams({
        searchParams: buildGiftOrderSearchParams({
          donationBusinessId,
          institutionId,
          institutionName
        })
      }));
    };
    $[11] = donationBusinessId;
    $[12] = institutionId;
    $[13] = institutionName;
    $[14] = router;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const handleDonationWithoutGiftAfterAgreement = t6;
  let t7;
  if ($[16] !== className) {
    t7 = cn("w-full", className);
    $[16] = className;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const t8 = children || "\uAE30\uBD80\uD558\uAE30";
  let t9;
  if ($[18] !== disabled || $[19] !== handleStart || $[20] !== isVerificationLoading || $[21] !== style || $[22] !== t7 || $[23] !== t8) {
    t9 = <Button loading={isVerificationLoading} onClick={handleStart} className={t7} disabled={disabled} style={style}>{t8}</Button>;
    $[18] = disabled;
    $[19] = handleStart;
    $[20] = isVerificationLoading;
    $[21] = style;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const t10 = step === "agreement";
  let t11;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setStep("closed");
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      title: "\uACE0\uD5A5\uC0AC\uB791\uAE30\uBD80 \uC11C\uBE44\uC2A4\uB97C \uC774\uC6A9\uD558\uAE30 \uC704\uD574 \uAF2D \uD544\uC694\uD55C \uB3D9\uC758\uB9CC \uCD94\uB838\uC5B4\uC694"
    };
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== handleDonationWithoutGiftAfterAgreement) {
    t13 = <AgreementSection onNext={handleDonationWithoutGiftAfterAgreement} />;
    $[27] = handleDonationWithoutGiftAfterAgreement;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t10 || $[30] !== t13) {
    t14 = <ResponsiveDialog opened={t10} onClose={t11} drawerType="bottom" title={"\uACE0\uD5A5\uC0AC\uB791\uAE30\uBD80 \uC11C\uBE44\uC2A4\uB97C \uC774\uC6A9\uD558\uAE30 \uC704\uD574 \uAF2D \uD544\uC694\uD55C \uB3D9\uC758\uB9CC \uCD94\uB838\uC5B4\uC694"} modalHeader={t12}>{t13}</ResponsiveDialog>;
    $[29] = t10;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const t15 = step === "product-check";
  let t16;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setStep("closed");
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      title: "\uAE30\uBD80 \uAE08\uC561\uC5D0 \uB9DE\uB294\n\uB2F5\uB840\uD488\uC744 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694",
      description: `고향사랑기부금의 ${GIFT_VALUE_RATE_PERCENT}% 내에서 답례품을 무료로 받아보세요!`
    };
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== isDesktopViewport) {
    t18 = !isDesktopViewport ? <><h3 className="break-keep text-center font-700 text-Gray-900 typo-body1">기부 금액에 맞는<br />답례품을 선택해 주세요</h3><p className="mb-24 mt-8 text-center font-400 text-Gray-800 typo-body3">고향사랑기부금의 {GIFT_VALUE_RATE_PERCENT}% 내에서<br />답례품을 무료로 받아보세요!</p></> : null;
    $[34] = isDesktopViewport;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setStep("closed");
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== institutionId || $[38] !== institutionSlugId) {
    t20 = DONATION_ROUTES.HOMETOWNS_DETAIL.withSearchParams({
      dynamicPath: {
        id: institutionSlugId ?? institutionId
      },
      searchParams: {
        tab: "gifts"
      }
    });
    $[37] = institutionId;
    $[38] = institutionSlugId;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== institutionName) {
    t21 = gtmDatasets.startGift({
      local_name: institutionName
    });
    $[40] = institutionName;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== t20 || $[43] !== t21) {
    t22 = <Button dimensions="H48" asChild={true}><CustomLink onClick={t19} href={t20} {...t21}>답례품 선택 후 기부하기</CustomLink></Button>;
    $[42] = t20;
    $[43] = t21;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== institutionName) {
    t23 = gtmDatasets.startDonation({
      local_name: institutionName
    });
    $[45] = institutionName;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== handleDonationWithoutGift || $[48] !== t23) {
    t24 = <Button dimensions="H48" theme="sub-text" onClick={handleDonationWithoutGift} {...t23}>답례품 없이 기부하기</Button>;
    $[47] = handleDonationWithoutGift;
    $[48] = t23;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== t22 || $[51] !== t24) {
    t25 = <div className="flex flex-col gap-8">{t22}{t24}</div>;
    $[50] = t22;
    $[51] = t24;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] !== t15 || $[54] !== t18 || $[55] !== t25) {
    t26 = <ResponsiveDialog opened={t15} onClose={t16} drawerType="bottom" ariaLabel={"\uAE30\uBD80 \uAE08\uC561\uC5D0 \uB9DE\uB294 \uB2F5\uB840\uD488\uC744 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694"} hideDrawerHeader={true} modalHeader={t17}>{t18}{t25}</ResponsiveDialog>;
    $[53] = t15;
    $[54] = t18;
    $[55] = t25;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  const t27 = step === "prefill";
  let t28;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = () => setStep("closed");
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = {
      title: "\uAE30\uBD80\uAE08 \uC120\uD0DD"
    };
    $[58] = t29;
  } else {
    t29 = $[58];
  }
  let t30;
  if ($[59] !== donationBusinessId || $[60] !== institutionId || $[61] !== institutionName || $[62] !== isKakaobank || $[63] !== router) {
    t30 = data_0 => {
      setStep("closed");
      if (!isKakaobank) {
        sessionStorage.setItem(STORAGE_KEY.DONATION_AGREEMENT, "true");
      }
      router.push(DONATION_ROUTES.HOMETOWN_BUSINESS_ORDER.withSearchParams({
        searchParams: buildGiftOrderSearchParams({
          donationBusinessId,
          institutionId,
          institutionName,
          initialAmount: data_0.amount,
          initialPurpose: data_0.purpose
        })
      }));
    };
    $[59] = donationBusinessId;
    $[60] = institutionId;
    $[61] = institutionName;
    $[62] = isKakaobank;
    $[63] = router;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] !== donationBusinessId || $[66] !== institutionId || $[67] !== isKakaobank || $[68] !== t30) {
    t31 = <PrefillForm institutionId={institutionId} defaultDonationBusinessId={donationBusinessId} hideAgreements={isKakaobank} onSubmit={t30} />;
    $[65] = donationBusinessId;
    $[66] = institutionId;
    $[67] = isKakaobank;
    $[68] = t30;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  let t32;
  if ($[70] !== t27 || $[71] !== t31) {
    t32 = <ResponsiveDialog opened={t27} onClose={t28} drawerType="bottom" modalHeader={t29} title={"\uAE30\uBD80\uAE08 \uC120\uD0DD"}>{t31}</ResponsiveDialog>;
    $[70] = t27;
    $[71] = t31;
    $[72] = t32;
  } else {
    t32 = $[72];
  }
  let t33;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = () => setIsEdgeModal(false);
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  let t34;
  let t35;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <h2 className="font-700 text-Gray-900 typo-body2">웰로의 고향사랑기부제는 엣지 브라우저에서 지원되지 않습니다.</h2>;
    t35 = <p className="font-400 text-Gray-700 typo-body3">원활한 이용을 위해 Chrome, Safari 등의 브라우저를 이용해 주세요.</p>;
    $[74] = t34;
    $[75] = t35;
  } else {
    t34 = $[74];
    t35 = $[75];
  }
  let t36;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <div className="flex flex-col gap-16 text-center">{t34}{t35}<Button onClick={() => setIsEdgeModal(false)}>확인</Button></div>;
    $[76] = t36;
  } else {
    t36 = $[76];
  }
  let t37;
  if ($[77] !== isEdgeModal) {
    t37 = <Modal opened={isEdgeModal} onClose={t33}>{t36}</Modal>;
    $[77] = isEdgeModal;
    $[78] = t37;
  } else {
    t37 = $[78];
  }
  let t38;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = () => setIsReservationModalOpen(false);
    $[79] = t38;
  } else {
    t38 = $[79];
  }
  let t39;
  if ($[80] !== startTransition) {
    t39 = () => {
      setIsReservationModalOpen(false);
      startTransition(() => {
        setIsReservationCompleteModalOpen(true);
      });
    };
    $[80] = startTransition;
    $[81] = t39;
  } else {
    t39 = $[81];
  }
  const t40 = donationReservation?.contents;
  let t41;
  if ($[82] !== donationBusinessId || $[83] !== isReservationModalOpen || $[84] !== t39 || $[85] !== t40) {
    t41 = <DonationReservationModal opened={isReservationModalOpen} businessId={donationBusinessId} onClose={t38} onSuccess={t39} description={t40} />;
    $[82] = donationBusinessId;
    $[83] = isReservationModalOpen;
    $[84] = t39;
    $[85] = t40;
    $[86] = t41;
  } else {
    t41 = $[86];
  }
  let t42;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = () => setIsReservationCompleteModalOpen(false);
    $[87] = t42;
  } else {
    t42 = $[87];
  }
  let t43;
  if ($[88] !== isReservationCompleteModalOpen) {
    t43 = <DonationReservationCompleteModal opened={isReservationCompleteModalOpen} onClose={t42} />;
    $[88] = isReservationCompleteModalOpen;
    $[89] = t43;
  } else {
    t43 = $[89];
  }
  let t44;
  if ($[90] !== t14 || $[91] !== t26 || $[92] !== t32 || $[93] !== t37 || $[94] !== t41 || $[95] !== t43 || $[96] !== t9) {
    t44 = <>{t9}{t14}{t26}{t32}{t37}{t41}{t43}</>;
    $[90] = t14;
    $[91] = t26;
    $[92] = t32;
    $[93] = t37;
    $[94] = t41;
    $[95] = t43;
    $[96] = t9;
    $[97] = t44;
  } else {
    t44 = $[97];
  }
  return t44;
};
function _temp(state) {
  return !!state.isLogin;
}
function _temp2(data) {
  return data.context;
}